import { FileText, FileSpreadsheet, CheckCircle2, Building2 } from "lucide-react";

export const Panel = ({ title, right, children, className = "" }) => (
  <div className={`glass overflow-hidden rounded-2xl ${className}`}>
    <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-teal/80" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

export const StatusPill = ({ tone = "teal", children }) => {
  const tones = {
    teal: "text-teal border-teal/30 bg-teal/10",
    amber: "text-amber-300 border-amber-300/30 bg-amber-300/10",
    slate: "text-slate-400 border-white/10 bg-white/5",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] ${tones[tone]}`}>
      <span className="h-1 w-1 rounded-full bg-current animate-pulse-dot" />
      {children}
    </span>
  );
};

export const AnalysisCard = ({ compact = false }) => {
  const rows = [
    ["Basic Salary", "2 140 200", "2 158 900", "2 161 400"],
    ["Overtime", "86 400", "92 150", "78 900"],
    ["Medical Aid", "312 800", "312 800", "314 100"],
    ["PAYE + UIF", "768 320", "771 540", "769 980"],
  ];
  return (
    <Panel title="Monthly Analysis" right={<StatusPill>Resolved</StatusPill>} className="w-full">
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[11px] text-slate-500">Period</span>
          <span className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[10px] text-slate-300">Mar 2026</span>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-600">
              <th className="pb-1.5 font-medium">Component</th>
              <th className="pb-1.5 text-right font-medium">Jan</th>
              <th className="pb-1.5 text-right font-medium">Feb</th>
              <th className="pb-1.5 text-right font-medium">Mar</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, compact ? 3 : 4).map((r) => (
              <tr key={r[0]} className="border-t border-white/[0.05] text-[11px] text-slate-300">
                <td className="py-1.5 text-slate-400">{r[0]}</td>
                <td className="py-1.5 text-right font-mono">{r[1]}</td>
                <td className="py-1.5 text-right font-mono">{r[2]}</td>
                <td className="py-1.5 text-right font-mono">{r[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-teal/25 bg-teal/[0.07] px-3 py-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-teal/90">Cost to Company</span>
          <span className="font-mono text-sm font-semibold text-teal">R 4,286,000</span>
        </div>
      </div>
    </Panel>
  );
};

export const CompanySelectorCard = () => (
  <Panel title="Companies" right={<span className="font-mono text-[9px] text-slate-500">3 entities</span>}>
    <ul className="px-2 py-2">
      {["Northgate Retail", "Helix Manufacturing", "Aurora Logistics"].map((c, i) => (
        <li
          key={c}
          className={`flex items-center justify-between rounded-lg px-2.5 py-2 text-[11px] ${
            i === 0 ? "bg-teal/[0.08] text-slate-100" : "text-slate-400"
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className={`h-3.5 w-3.5 ${i === 0 ? "text-teal" : "text-slate-600"}`} />
            {c}
          </span>
          {i === 0 && <CheckCircle2 className="h-3.5 w-3.5 text-teal" />}
        </li>
      ))}
    </ul>
  </Panel>
);

export const MonthEndPackCard = () => (
  <Panel title="Month End Pack" right={<StatusPill>Consolidated</StatusPill>}>
    <div className="px-4 py-3">
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>Reports organised</span>
        <span className="font-mono text-slate-300">14 / 14</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
        <div className="h-full w-full rounded-full bg-gradient-to-r from-cy to-teal shadow-[0_0_12px_rgba(0,245,212,0.5)]" />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono text-[10px] text-slate-500">3 companies · 1 pack</span>
        <span className="rounded-md border border-teal/30 bg-teal/10 px-2 py-1 font-mono text-[10px] text-teal">Export</span>
      </div>
    </div>
  </Panel>
);

export const HeadcountCard = () => (
  <Panel title="Workforce" right={<StatusPill tone="slate">Live</StatusPill>}>
    <div className="px-4 py-3">
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Headcount</p>
      <p className="mt-1 font-display text-3xl font-semibold text-slate-50">1,284</p>
      <div className="mt-2 flex items-center gap-3 font-mono text-[10px]">
        <span className="text-teal">+36 joiners</span>
        <span className="text-slate-500">−12 exits</span>
      </div>
    </div>
  </Panel>
);

export const OrgCard = () => (
  <Panel title="Organisation">
    <div className="flex flex-col items-center gap-1.5 px-4 py-3">
      <span className="h-2 w-2 rounded-sm bg-teal" />
      <span className="h-2 w-px bg-white/20" />
      <div className="flex gap-3">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-2 w-2 rounded-sm bg-cy/70" />
        ))}
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <span key={i} className="h-1.5 w-1.5 rounded-full bg-slate-500/70" />
        ))}
      </div>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Exec → Teams → People</p>
    </div>
  </Panel>
);

export const ExportChip = ({ type = "pdf", className = "" }) => {
  const Icon = type === "pdf" ? FileText : FileSpreadsheet;
  return (
    <span className={`glass inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300 ${className}`}>
      <Icon className={`h-3.5 w-3.5 ${type === "pdf" ? "text-teal" : "text-cy"}`} />
      {type === "pdf" ? "PDF" : "Excel"}
    </span>
  );
};

export const WorkflowStatusCard = () => (
  <Panel title="Payroll Automation" right={<StatusPill tone="amber">Running</StatusPill>}>
    <div className="px-4 py-3">
      <div className="flex items-center gap-1.5">
        {["Extract", "Validate", "Pack"].map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span
              className={`rounded-md border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.12em] ${
                i < 2 ? "border-teal/30 bg-teal/10 text-teal" : "border-white/10 bg-white/[0.04] text-slate-400"
              }`}
            >
              {s}
            </span>
            {i < 2 && <span className="h-px w-2.5 bg-teal/40" />}
          </span>
        ))}
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        <span className="font-mono text-[9px] text-slate-500">Close steps · monthly</span>
        <span className="font-mono text-[9px] text-slate-400">Step 3 / 3</span>
      </div>
    </div>
  </Panel>
);

export const ReportChip = ({ name, done = true, className = "" }) => (
  <span className={`glass-soft inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-mono text-[10px] text-slate-300 ${className}`}>
    <FileText className={`h-3 w-3 ${done ? "text-teal" : "text-slate-500"}`} />
    {name}
  </span>
);
